import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HomeOutlinedIcon, AddIcon } from "../../../icons";
import GlobalLoader from "../../common/GlobalLoader";
import FolderPopup from "../../common/FolderPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import {
  getImageFolders,
  createImageFolder,
  updateImageFolder,
  deleteImageFolder,
} from "../../../helper/apicalls/imageUpload";
import "../../../styles/admin/imageUpload.scss";
import openFolderIcon from "../../../assets/png/openFolderIcon.png";

const ImageUploadFolderPage = () => {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFolderPopup, setShowFolderPopup] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    isOpen: false,
    folderId: null,
    folderName: "",
  });

  useEffect(() => {
    if (!menuOpenId) return;

    const handleClickOutside = (event) => {
      const menus = document.querySelectorAll(
        ".folder-menu, .folder-menu-button",
      );
      for (let i = 0; i < menus.length; i += 1) {
        const el = menus[i];
        if (el && el.contains(event.target)) {
          return;
        }
      }

      setMenuOpenId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpenId]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getImageFolders();
      const data = response?.data || response?.folders || response || [];
      setFolders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching image folders:", err);
      setError("Failed to fetch folders");
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const handleCreateFolder = () => {
    setEditingFolder(null);
    setShowFolderPopup(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setShowFolderPopup(true);
  };

  const handleDeleteFolder = async (folder) => {
    setConfirmDelete({
      isOpen: true,
      folderId: folder.id,
      folderName: folder.name || folder.id,
    });
  };

  const confirmDeleteFolder = async () => {
    const { folderId } = confirmDelete;
    try {
      await deleteImageFolder(folderId);
      await fetchFolders();
      setConfirmDelete({ isOpen: false, folderId: null, folderName: "" });
    } catch (err) {
      console.error("Error deleting folder:", err);
      alert("Failed to delete folder");
    }
  };

  const handleFolderSave = async (folderData) => {
    try {
      if (editingFolder) {
        await updateImageFolder(editingFolder.id, folderData);
      } else {
        await createImageFolder(folderData);
      }
      setShowFolderPopup(false);
      setEditingFolder(null);
      await fetchFolders();
    } catch (err) {
      console.error("Error saving folder:", err);
      alert("Failed to save folder");
    }
  };

  const handleFolderClick = (folder) => {
    navigate(`/admin/image-folders/${folder.id}`);
  };

  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="image-upload-container">
        <div className="image-upload-card">
          <div className="image-upload-header">
            <div className="header-left">
              <span className="page-title">Image Folders</span>
            </div>
            <div className="header-actions">
              <button className="primary-btn" onClick={handleCreateFolder}>
                <AddIcon />
                Create Folder
              </button>
            </div>
          </div>
          <div className="folder-grid custom-scrollbar">
            {!loading &&
              !error &&
              folders?.map((folder) => (
                <div key={folder.id} className="folder-item">
                  <div
                    className="folder-content"
                    onClick={() => handleFolderClick(folder)}
                    style={{ cursor: "pointer", flex: 1 }}
                  >
                    <div className="folder-icon">
                      <img
                        src={openFolderIcon}
                        alt="Folder"
                        style={{ width: "72px", height: "72px" }}
                      />
                    </div>
                    <div
                      className="folder-name"
                      title={folder.name || folder.id}
                    >
                      {folder.name || folder.id}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="folder-menu-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId((prev) =>
                        prev === folder.id ? null : folder.id,
                      );
                    }}
                  >
                    ⋮
                  </button>
                  {menuOpenId === folder.id && (
                    <div
                      className="folder-menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="folder-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditFolder(folder);
                          setMenuOpenId(null);
                        }}
                      >
                        Edit
                      </div>
                      <div
                        className="folder-menu-item --danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFolder(folder);
                          setMenuOpenId(null);
                        }}
                      >
                        Delete
                      </div>
                    </div>
                  )}
                </div>
              ))}

            {error && (
              <div className="error-state">
                <div className="error-message">{error}</div>
              </div>
            )}

            {!loading && !error && folders.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">
                  <img
                    src={openFolderIcon}
                    alt="Folder"
                    style={{ width: "72px", height: "72px" }}
                  />
                </div>
                <h3 className="empty-title">No folders available</h3>
                <p className="empty-description">
                  Create your first folder to start organizing your images
                </p>
                <button
                  className="empty-action-btn"
                  onClick={handleCreateFolder}
                >
                  <AddIcon />
                  Create First Folder
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFolderPopup && (
        <FolderPopup
          open={showFolderPopup}
          handleClose={() => {
            setShowFolderPopup(false);
            setEditingFolder(null);
          }}
          folder={editingFolder}
          onSave={handleFolderSave}
        />
      )}

      {confirmDelete.isOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={confirmDelete.isOpen}
          setIsConfirmDeleteOpen={(isOpen) =>
            setConfirmDelete({ isOpen, folderId: null, folderName: "" })
          }
          handleDelete={confirmDeleteFolder}
          title="Delete Folder"
          message={`Are you sure you want to delete the folder "${confirmDelete.folderName}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
      )}
    </>
  );
};

export default ImageUploadFolderPage;
